import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FaRocket, FaShieldAlt, FaUsers, FaChartLine, FaGlobe, FaHeadset } from 'react-icons/fa';
import Header from '../components/Header';

const About = () => {
  useEffect(() => {
    document.title = 'About AppActs - The App Activation Platform for Vibe Coders';
  }, []);

  const features = [
    {
      icon: FaShieldAlt,
      title: "Legal Compliance",
      description: "Generate GDPR-compliant legal documents in minutes with our intelligent document generator."
    },
    {
      icon: FaGlobe,
      title: "Smart URL Management",
      description: "Create intelligent short links with platform-specific redirects and detailed analytics."
    },
    {
      icon: FaHeadset,
      title: "Customer Support",
      description: "Professional customer support systems with ticket management and priority handling."
    },
    {
      icon: FaRocket,
      title: "Business Growth",
      description: "Internal ads and landing page tools to promote your products and increase conversions."
    },
    {
      icon: FaChartLine,
      title: "Analytics & Insights",
      description: "Comprehensive analytics to track performance and make data-driven decisions."
    },
    {
      icon: FaUsers,
      title: "Team Collaboration",
      description: "Multi-project support for teams and agencies managing multiple clients."
    }
  ];

  return (
    <>
      <Helmet>
        <title>About AppActs - The App Activation Platform for Vibe Coders</title>
        <meta name="description" content="Learn about AppActs, the comprehensive business solutions platform designed for indie developers and vibe coders. Legal documents, URL shortening, customer support, and more." />
        <meta name="keywords" content="AppActs, about, business solutions, legal documents, URL shortener, customer support, indie developers, vibe coders" />
        <meta property="og:title" content="About AppActs - The App Activation Platform" />
        <meta property="og:description" content="Learn about AppActs, the comprehensive business solutions platform designed for indie developers and vibe coders." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About AppActs - The App Activation Platform" />
        <meta name="twitter:description" content="Learn about AppActs, the comprehensive business solutions platform designed for indie developers and vibe coders." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-black via-purple-900/40 to-indigo-900/50">
        <Header />
        {/* Hero Section */}
        <section className="py-32 bg-gradient-to-br from-black via-purple-900/40 to-indigo-900/50 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-5xl md:text-7xl font-light mb-6 leading-tight">
                About AppActs
              </h1>
              <p className="text-xl md:text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed">
                The App Activation Platform designed for indie developers and vibe coders
              </p>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-gradient-to-br from-black via-purple-900/40 to-indigo-900/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-light text-white mb-6">
                  Our Mission
                </h2>
                <p className="text-xl text-white/70 mb-8 leading-relaxed">
                  AppActs is your complete business solutions platform, designed to help indie developers and vibe coders grow and succeed. Our mission is to provide comprehensive tools for legal compliance, URL management, customer support, and business growth—all in one unified platform.
                </p>
                <p className="text-lg text-white/60 leading-relaxed">
                  We started with a simple goal: making legal document generation accessible and affordable for everyone. Today, we've evolved into a comprehensive platform that includes smart URL shortening with analytics, professional customer support systems, and premium business features.
                </p>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 h-96 flex items-center justify-center">
                <div className="text-center text-white/50">
                  <FaRocket className="h-24 w-24 mx-auto mb-4" />
                  <p className="text-lg">Mission Visualization</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gradient-to-br from-black via-purple-900/40 to-indigo-900/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-light text-white mb-4">
                What We Offer
              </h2>
              <p className="text-xl text-white/70 max-w-2xl mx-auto">
                Comprehensive tools designed specifically for indie developers and vibe coders
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 transition-transform duration-200 hover:scale-105">
                  <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-4">
                    <feature.icon className="h-8 w-8 text-white/80" />
                  </div>
                  <h3 className="text-xl font-light text-white mb-3">{feature.title}</h3>
                  <p className="text-white/70 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Legal Disclaimer */}
        <section className="py-20 bg-gradient-to-br from-black via-purple-900/40 to-indigo-900/50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
              <h3 className="text-2xl font-light text-white mb-6">Legal Disclaimer</h3>
              <p className="text-white/70 leading-relaxed">
                Our legal document generator and business tools are provided for informational purposes only and do not constitute 
                legal advice. The generated documents are based on common legal requirements, but every business 
                is unique. We recommend reviewing any generated documents with a qualified legal professional to ensure 
                they meet your specific needs and comply with all applicable laws in your jurisdiction. We make no 
                warranties about the accuracy, completeness, or adequacy of the generated documents for any particular purpose.
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About; 